<template>
  <WdsStack direction-column class="milestone-block">
    <table class="tw-mb-4">
      <colgroup>
        <col v-if="milestoneContainsStatus" style="width: 30px; white-space: nowrap" />
        <col style="width: 30%" />
        <col style="width: auto" />
        <col style="width: auto" />
        <col style="width: auto" />
        <col
          v-if="milestoneBlockState === MilestoneEditState.editing"
          style="width: 30px; white-space: nowrap"
        />
      </colgroup>
      <tbody>
        <tr>
          <th v-if="milestoneContainsStatus"><!-- Room for status --></th>
          <th>
            <WdsText>{{ $t('Milestone name') }}</WdsText>
          </th>
          <th>
            <WdsText>{{ $t('Po number') }}</WdsText>
          </th>
          <th>
            <WdsText>{{ $t('Due date') }}</WdsText>
          </th>
          <th>
            <WdsText>{{ $t('Amount') }}</WdsText>
          </th>
          <th v-if="milestoneBlockState === MilestoneEditState.editing">
            <!-- Room for trashcan -->
          </th>
        </tr>
        <Milestone
          v-for="(m, index) of milestones"
          :key="m.id"
          v-model="milestones[index]"
          :milestone-block-state="milestoneBlockState"
          :show-status-column="milestoneContainsStatus"
          :currency
          @delete="removeMilestone"
        />
      </tbody>
    </table>
    <WdsStack justify-space-between align-center class="milestone-action-bar">
      <WdsButton
        v-if="milestoneBlockState === MilestoneEditState.editing"
        sm
        secondary
        data-test="add-milestone"
        @click.prevent="addMilestone"
      >
        <WdsStack justify-center align-center gap-xs>
          <WdsIcon name="plus" />
          {{ $t('Add milestone') }}
        </WdsStack>
      </WdsButton>
      <div v-else></div>
      <WdsStack class="milestone-total" gap-md justify-center align-center>
        <WdsText large>
          {{ $t('Total:') }}
        </WdsText>
        <WdsStack direction-column>
          <WdsText large>
            <strong>{{ currency }} {{ totalRate }}</strong>
          </WdsText>
          <WdsText v-if="rateChanged" strike-through tertiary large>
            <strong>{{ currency }} {{ originalRate }}</strong>
          </WdsText>
        </WdsStack>
        <WdsTooltip :text="$t('Total of all milestone amounts added.')">
          <WdsStack justify-center align-center>
            <WdsIcon weight="thin" name="help-filled" :size="16" color="#9999AA" />
          </WdsStack>
        </WdsTooltip>
      </WdsStack>
    </WdsStack>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { WdsButton, WdsIcon, WdsStack, WdsText, WdsTooltip } from '@worksome/wds-components'
import Milestone from '@/components/Milestones/Milestone.vue'
import { MilestoneEditState } from '@/components/Milestones/types'
import { useTranslation } from '@/composables/translation'
import { MilestoneTypeFragment, MilestoneStatus } from '@/gql/graphql'
import { uuid } from '@/utils/tools'

const { $t } = useTranslation()

const DEFAULT_PROP_RATE = '-1'

const milestones = defineModel<MilestoneTypeFragment[]>({ required: true })

const props = withDefaults(
  defineProps<{
    milestoneBlockState: MilestoneEditState
    currency?: string
    originalRate?: string
  }>(),
  {
    currency: () => currentAccount.currency,
    originalRate: () => DEFAULT_PROP_RATE, // we can't have negatives in rates so this communicates that there is no rate
  },
)

const addMilestone = () => {
  milestones.value.push({
    id: uuid(),
    name: '',
    purchaseOrderNumber: '',
    status: MilestoneStatus.Pending,
    details: {
      id: uuid(),
      dueDate: '',
      amount: 0,
    },
  })
}

const totalRate = computed(() =>
  milestones.value.reduce((count, milestone) => {
    const milestoneDetails = milestone.details

    return (parseFloat(count) + milestoneDetails.amount || 0).toFixed(2)
  }, '0'),
)

const rateChanged = computed(
  () => props.originalRate !== DEFAULT_PROP_RATE && props.originalRate !== totalRate.value,
)

const removeMilestone = (milestone: MilestoneTypeFragment) => {
  const index = milestones.value.map(({ id }) => id).indexOf(milestone.id)
  if (index > -1) {
    milestones.value.splice(index, 1)
  }
}

const milestoneContainsStatus = computed(() =>
  milestones.value.some((milestone) => milestone.status !== MilestoneStatus.Pending),
)

watch(
  milestones,
  () => {
    if (milestones.value.length === 0) {
      addMilestone() // If we ever hit an empty state we add a new clean milestone by default!
    }
  },
  { immediate: true, deep: true },
)
</script>

<style lang="scss" scoped>
@tailwind base;

.milestone-block {
  table {
    width: 100%;
    table-layout: fixed;
  }

  .milestone-action-bar {
    padding: 10px 8px;
    background: var(--default-true-gray-truegray-50-fafafa, #fafafa);
    @apply tw-rounded;
    .milestone-total {
      padding-left: 16px;
      border-left: 2px solid #dfdfe6;
    }
  }
}
</style>
